/**
 * ReturnsScreen component handles the display and processing of return receipts.
 *
 * @component
 * @example
 * return (
 *   <ReturnsScreen />
 * )
 *
 * @returns {JSX.Element} The ReturnsScreen component.
 *
 * @description
 * This component is responsible for displaying return receipts and handling the return process.
 * It includes functionalities such as showing alerts, navigating to different routes, and dispatching actions.
 *
 * @property {string} barcode - The barcode of the item to be returned.
 * @property {Object} alert - The alert state object.
 * @property {boolean} alert.show - Whether the alert is shown.
 * @property {string} alert.message - The alert message.
 * @property {string} alert.variant - The alert variant (e.g., 'success', 'info').
 *
 * @function showAlert
 * @description Shows an alert with a specified message and variant for a given duration.
 * @param {string} message - The message to be displayed in the alert.
 * @param {string} [variant='success'] - The variant of the alert.
 * @param {number} [seconds=1500] - The duration for which the alert is shown.
 *
 * @function submitHandler
 * @description Handles the form submission, resets the barcode, dispatches a reset action, and navigates to the PoS screen.
 * @param {Object} e - The event object.
 *
 * @hook useEffect
 * @description Redirects to the login screen if the user is not logged in.
 * @param {Function} dispatch - The dispatch function from Redux.
 * @param {Function} navigate - The navigate function from react-router-dom.
 * @param {Object} userInfo - The user information from the Redux state.
 *
 * @hook useEffect
 * @description Shows an alert if no return receipt is found and resets the returns state.
 * @param {Function} dispatch - The dispatch function from Redux.
 * @param {number} returncount - The count of return receipts.
 *
 * @hook useEffect
 * @description Shows a success alert if a return receipt is found and navigates to the returns screen.
 * @param {Function} dispatch - The dispatch function from Redux.
 * @param {Function} navigate - The navigate function from react-router-dom.
 * @param {boolean} success - The success status of the return receipt fetch.
 * @param {string} barcode - The barcode of the item to be returned.
 */
import React, { useContext, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Form, Button, Container, Row, Col, Alert } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import Message from '../components/Message'
import Loader from '../components/Loader'
import moment from 'moment'
import BarcodeSearch from '../components/BarcodeSearch'

// Import icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheckCircle,
  faTimesCircle
} from '@fortawesome/free-regular-svg-icons'
import { getreturnsReceipt, returnsUpdate } from '../actions/posActions'
import { RETURNS_UPDATE_RESET, RETURNS_RESET } from '../constants/posConstants'
import { API_URL } from '../apiUrl'
import { ThemeContext } from '../components/ThemeContext.js'

// Begin ReturnsScreen
const ReturnsScreen = () => {
  const { theme } = useContext(ThemeContext)
  const receiptBaseUrl = API_URL.replace(/\/api\/?$/, '')

  const [alert, setAlert] = useState({
    show: false,
    message: '',
    variant: 'warning'
  })

  const showAlert = (message, variant = 'warning', seconds = 1800) => {
    setAlert({
      show: true,
      message,
      variant
    })

    setTimeout(() => {
      setAlert({
        show: false,
        message: '',
        variant: 'success'
      })
    }, seconds)
  }

  // Initialize dispatch and navigate
  const dispatch = useDispatch()
  const navigate = useNavigate()

  // Get user info
  const userLogin = useSelector((state) => state.userLogin)
  const { userInfo } = userLogin

  const ownerLogin = useSelector((state) => state.ownerLogin)
  const { ownerInfo } = ownerLogin

  const activeSession = ownerInfo || userInfo

  // Get returns receipt
  const returnsReceipt = useSelector((state) => state.returnsReceipt)
  const { returnreceipt, loading, error, success, returncount } = returnsReceipt

  const hasLoadedReceipt = Boolean(returnreceipt && returnreceipt.barcode)

  // Get returned receipt
  const returnedUpdate = useSelector((state) => state.returnedUpdate)
  const { returnedreceipt } = returnedUpdate

  // Redirect to login if not logged in
  useEffect(() => {
    if (!activeSession) {
      dispatch({ type: RETURNS_RESET })
      dispatch({ type: RETURNS_UPDATE_RESET })
      navigate('/login')
    }
  }, [dispatch, navigate, activeSession])

  // Show alert if no return receipt found
  useEffect(() => {
    if (returncount === 0 && success === true) {
      showAlert('No return receipt found', 'info')
      dispatch({ type: RETURNS_RESET })
    }
  }, [dispatch, returncount, success])

  // Show alert if return receipt found
  useEffect(() => {
    if (returnreceipt && returnreceipt.returned === true) {
      showAlert('Receipt has been returned', 'warning')
      navigate('/returns')
      dispatch({ type: RETURNS_UPDATE_RESET })
    }
  }, [dispatch, navigate, returnreceipt])

  // Update with return receipt
  useEffect(() => {
    if (returnedreceipt) {
      dispatch(getreturnsReceipt(returnedreceipt.barcode, returnedreceipt.cid))
      navigate('/returns')
    }
  }, [dispatch, navigate, returnedreceipt])

  // Prevent render on logout
  if (!activeSession) {
    return null
  }

  // Handle form submission
  const submitHandler = (e) => {
    e.preventDefault()
    dispatch({ type: RETURNS_RESET })
    navigate('/pos')
  }

  const resetReceiptHandler = () => {
    dispatch({ type: RETURNS_RESET })
  }

  // Handle returns update
  const returnsUpdateHandler = (e) => {
    e.preventDefault()

    if (!returnreceipt?.barcode || !returnreceipt?.cid) {
      showAlert('No return receipt loaded', 'warning')
      return
    }

    const returninfo = {
      barcode: returnreceipt.barcode,
      cid: returnreceipt.cid
    }

    dispatch(returnsUpdate(returninfo))
  }

  // Update receipt screen
  return (
    <Container style={{ color: theme.text }}>
      {alert.show && <Alert variant={alert.variant}>{alert.message}</Alert>}
      {loading && <Loader />}
      {error && <Message variant='danger'>{error}</Message>}

      <Row className='my-2'>
        <h4>Process Return Receipt</h4>
        <Col>
          <Form onSubmit={submitHandler}>
            <Form.Group controlId='posreturn'></Form.Group>
            <Button type='submit' variant='outline-secondary' className='my-1'>
              Back to PoS
            </Button>
          </Form>
        </Col>
      </Row>
      {activeSession.cid && !hasLoadedReceipt && (
        <Row className='my-2'>
          <BarcodeSearch />
        </Row>
      )}

      <Row>
        <Col>
          {hasLoadedReceipt && (
            <h5>
              Receipt Date:{' '}
              {moment(returnreceipt.createdAt).format('ddd DD MMM YYYY h:mm A')}
            </h5>
          )}

          {hasLoadedReceipt && (
            <img
              src={`${receiptBaseUrl}/return/${returnreceipt.qfname}`}
              width={350}
              height='auto'
              alt='Receipt'
            />
          )}
        </Col>
        {hasLoadedReceipt && (
          <Col>
            <div className='mb-3'>
              <Button
                type='button'
                variant='outline-secondary'
                className='my-1'
                onClick={resetReceiptHandler}
              >
                Search Another Receipt
              </Button>
            </div>

            {returnreceipt.returned === false
              ? 'Items may be returned: '
              : 'Items have been returned - Receipt is closed: '}

            {returnreceipt.returned === false ? (
              <FontAwesomeIcon
                icon={faCheckCircle}
                style={{ color: theme.success }}
              />
            ) : (
              <FontAwesomeIcon
                icon={faTimesCircle}
                style={{ color: theme.danger }}
              />
            )}
            <br />

            {returnreceipt.returned === false && (
              <Form onSubmit={returnsUpdateHandler}>
                <Form.Group controlId='posreturn'></Form.Group>
                <Button
                  type='submit'
                  variant='outline-secondary'
                  className='my-1'
                >
                  Accept Return
                </Button>
              </Form>
            )}
          </Col>
        )}
      </Row>
    </Container>
  )
}

export default ReturnsScreen
